import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { CheckCircle, Package, ArrowRight, Truck, MapPin, Clock } from "lucide-react";
import { formatPrice } from "../utils/format";
import { getEstimatedDeliveryWindow } from "../utils/shipping";

export default function OrderSuccessPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    getDoc(doc(db, "orders", id)).then((s) => s.exists() && setOrder({ id: s.id, ...s.data() }));
  }, [id]);

  const deliveryWindow = order
    ? getEstimatedDeliveryWindow(order.createdAt || new Date(), 7)
    : null;

  const isPickup = order?.fulfillmentType === "pickup";

  return (
    <div
      style={{
        minHeight: "85vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--surface)",
        padding: "40px 20px",
      }}
    >
      <div style={{ maxWidth: 620, width: "100%", textAlign: "center" }}>
        <div
          style={{
            width: 80,
            height: 80,
            background: "#DCFCE7",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          <CheckCircle size={40} color="var(--success)" />
        </div>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 32, fontWeight: 800, marginBottom: 8 }}>
          Order Confirmed! 🎉
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: 16, marginBottom: 28 }}>
          Thank you for your purchase. We have received your payment and started processing your order.
        </p>

        {order && (
          <div
            className="card"
            style={{
              padding: 28,
              textAlign: "left",
              marginBottom: 28,
              background: "#fff",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            {/* Header info */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                paddingBottom: 14,
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 13,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Order #{order.id.slice(-8).toUpperCase()}
                </span>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  Payment Reference: {order.paymentRef || "Paid"}
                </div>
              </div>
              <span className="badge badge-success" style={{ padding: "6px 12px", fontSize: 12 }}>
                Paid & Confirmed
              </span>
            </div>

            {/* ⏱️ Guaranteed 7-Day Timeline Banner */}
            <div
              style={{
                padding: "14px 16px",
                background: "rgba(232, 71, 26, 0.05)",
                border: "1px solid rgba(232, 71, 26, 0.15)",
                borderRadius: 10,
                marginBottom: 20,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Clock size={22} color="var(--brand)" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
                  Guaranteed Delivery: Within 7 Days
                </div>
                <div style={{ fontSize: 13, color: "var(--brand)", fontWeight: 600, marginTop: 2 }}>
                  {isPickup
                    ? `Ready for pickup by ${order.estimatedDeliveryDate || deliveryWindow?.dateText}`
                    : `Delivered to your address by ${order.estimatedDeliveryDate || deliveryWindow?.dateText}`}
                </div>
              </div>
            </div>

            {/* Fulfillment Destination Box */}
            <div
              style={{
                padding: 16,
                background: "#F8FAFC",
                borderRadius: 10,
                border: "1px solid var(--border)",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 8,
                }}
              >
                {isPickup ? (
                  <>
                    <MapPin size={16} color="var(--brand)" />
                    <span>Station Pickup Location</span>
                  </>
                ) : (
                  <>
                    <Truck size={16} color="var(--brand)" />
                    <span>Door Delivery Destination</span>
                  </>
                )}
              </div>

              {isPickup ? (
                <div>
                  <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
                    🏪 {order.delivery?.pickupLocationName || order.pickupLocation?.name || "Afriport Pickup Hub"}
                  </p>
                  <p style={{ margin: "0 0 6px", color: "#475569", fontSize: 14 }}>
                    {order.delivery?.pickupAddress || order.pickupLocation?.address},{" "}
                    {order.delivery?.pickupCity || order.pickupLocation?.city},{" "}
                    {order.delivery?.state} State
                  </p>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", display: "flex", flexWrap: "wrap", gap: 12 }}>
                    {(order.delivery?.pickupPhone || order.pickupLocation?.phone) && (
                      <span>📞 {order.delivery?.pickupPhone || order.pickupLocation?.phone}</span>
                    )}
                    <span>
                      🕒 Hours: {order.delivery?.pickupHours || order.pickupLocation?.operatingHours || "Mon-Sat: 9am - 6pm"}
                    </span>
                  </div>
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--brand)", fontWeight: 500 }}>
                    ℹ️ You will receive an SMS and email notification once your order arrives at the station.
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: 14 }}>
                    {order.delivery?.fullName} · {order.delivery?.phone}
                  </p>
                  <p style={{ margin: 0, color: "#475569", fontSize: 14 }}>
                    {order.delivery?.address}, {order.delivery?.city}, {order.delivery?.state} State
                  </p>
                </div>
              )}
            </div>

            {/* Items */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 10,
                }}
              >
                Purchased Items
              </div>
              {order.items?.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid var(--border)",
                    fontSize: 14,
                  }}
                >
                  <span>
                    {item.name} × {item.qty}
                  </span>
                  <span style={{ fontWeight: 600 }}>{formatPrice(item.price * item.qty)}</span>
                </div>
              ))}
            </div>

            {/* Price Totals */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                <span>Subtotal</span>
                <span>{formatPrice(order.subtotal || order.total - (order.shipping || 0))}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                <span>{isPickup ? "Station Pickup" : "Delivery"}</span>
                <span>{order.shipping === 0 ? "Free" : formatPrice(order.shipping || 0)}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px solid var(--border)",
                  fontWeight: 800,
                }}
              >
                <span style={{ fontSize: 15 }}>Total Paid</span>
                <span style={{ color: "var(--brand)", fontFamily: "var(--font-head)", fontSize: 18 }}>
                  {formatPrice(order.total)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/account/orders" className="btn btn-secondary" style={{ padding: "12px 24px" }}>
            <Package size={16} /> Track My Orders
          </Link>
          <Link to="/shop" className="btn btn-primary" style={{ padding: "12px 24px" }}>
            Continue Shopping <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
